import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useLeadFormStore } from '../../store/leadFormStore';

const ProgressIndicator: React.FC = () => {
  const { progress } = useLeadFormStore();

  const stepConfig = [
    { title: 'Parent Info', description: 'Contact details' },
    { title: 'Address', description: 'Home address' },
    { title: 'Children', description: 'Child information' },
    { title: 'Schedule', description: 'Optional event' },
    { title: 'Documents', description: 'Upload files' },
    { title: 'Review', description: 'Submit lead' }
  ];

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < progress.currentStepIndex) return 'completed';
    if (stepIndex === progress.currentStepIndex) return 'current';
    return 'upcoming';
  };

  return (
    <nav aria-label="Progress" className="mb-8">
      {/* Desktop version */}
      <ol className="hidden sm:flex items-center justify-between">
        {stepConfig.map((config, index) => {
          const status = getStepStatus(index);
          return (
            <li key={index} className="relative flex-1">
              {/* Connector line */}
              {index < stepConfig.length - 1 && (
                <div 
                  className={`absolute top-4 left-1/2 w-full h-0.5 ${
                    status === 'completed' ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                  style={{ left: 'calc(50% + 20px)', width: 'calc(100% - 40px)' }}
                />
              )}
              
              {/* Step circle */}
              <div className="relative flex flex-col items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  status === 'completed' 
                    ? 'bg-primary-600 border-primary-600' 
                    : status === 'current'
                    ? 'border-primary-600 bg-white'
                    : 'border-gray-300 bg-white'
                }`}>
                  {status === 'completed' ? (
                    <CheckIcon className="h-5 w-5 text-white" />
                  ) : (
                    <span className={`text-sm font-medium ${
                      status === 'current' ? 'text-primary-600' : 'text-gray-500'
                    }`}>
                      {index + 1}
                    </span>
                  )}
                </div>
                
                {/* Step details */}
                <div className="mt-3 text-center">
                  <div className={`text-sm font-medium ${
                    status === 'current' ? 'text-primary-600' : 'text-gray-500'
                  }`}>
                    {config.title}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {config.description}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Mobile version */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium text-gray-900">
            Step {progress.currentStepIndex + 1} of {progress.totalSteps}
          </div>
          <div className="text-sm text-gray-500">
            {stepConfig[progress.currentStepIndex]?.title}
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${((progress.currentStepIndex + 1) / progress.totalSteps) * 100}%`,
            }}
          />
        </div>
      </div>
    </nav>
  );
};

export default ProgressIndicator;
